import React, { useEffect, useState } from 'react';
import { Col, Row, Container, UncontrolledTooltip } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { faCalendar, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import ReactTable from 'react-table';
import AutoChargeTableCols from './AutoChargeTableCols';
import { useAxios } from '../../../hooks';
import { imagePath } from '../../../utilities/UrlPaths';
import { formatDate } from '../../../utilities/invoiceUtils';
import GoogleTagManagerProvider from '../../../providers/GoogleTagManager';
import RemoveAutoChargeModal from './RemoveAutoChargeModal';

const InvoiceAutoCharge = () => {
  const { invoiceGroupID, invoiceIDList } = useParams();
  const invoiceIDStringArray = invoiceIDList.split(',');
  const urlInvoiceIDArray = invoiceIDStringArray.map(Number);
  // autoChargeEnabledPage is a page-level variable to control display
  const { houseRef } = useSelector(state => state.house);
  const [autoChargeEnabledPage, setAutoChargeEnabledPage] = useState(false);
  const [disabledText, setDisabledText] = useState('disabled-label');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(0);
  const [selectedDateMillis, setSelectedDateMillis] = useState('');
  const [showNewDates, setShowNewDates] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [submitButtonText, setSubmitButtonText] = useState('Continue With No Changes');
  const [defaultDateChanged, setDefaultDateChanged] = useState(false);
  const [apiInvoiceIdArray, setApiInvoiceIdArray] = useState([]);
  const [acInvoiceIdArray, setAcInvoiceIdArray] = useState([]);
  const [isEveryInvoiceChecked, setIsEveryInvoiceChecked] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [showRemoveLink, setShowRemoveLink] = useState(false);

  const previewURL = `${imagePath}/invoices/invoiceGroupPreview/${invoiceGroupID}/invoice/${invoiceIDList}`;
  const groupURL = `${imagePath}/invoices/InvoiceGroupDetails/${invoiceGroupID}`;

  const { data, isIdle, isLoading } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/autoCharge`,
    [houseRef],
    { method: 'post', data: urlInvoiceIDArray },
  );

  useEffect(() => {
    if (typeof data === 'undefined' || data === null) {
      return;
    }

    const invoicesArray = get(data, 'data.data.eligibleInvoiceList', []);
    setInvoices(invoicesArray);

    const acInvoiceIds = invoicesArray.map(item => item.invoiceID);
    setApiInvoiceIdArray(acInvoiceIds);
  }, [data]);

  const getEligibleDate = (dateObject) => {
    if (dateObject === null) return 0;

    const fixedYear = dateObject.year + 1900;
    const midnightTime = new Date(fixedYear, dateObject.month, dateObject.date, 0);
    return midnightTime.getTime();
  };

  useEffect(() => {
    if (invoices.length > 0) {
      const acInvoiceIds = invoices.map(item => item.invoiceID);
      setAcInvoiceIdArray(acInvoiceIds);

      const limitedFields = invoices.map(item => ({
        invoiceID: item.invoiceID,
        invoiceTotal: item.total,
        eligibleAmount: item.eligibleAmount,
        firstName: item.firstName,
        lastName: item.lastName,
        channel: item.channel,
        approvalID: item.approvalID,
        currencySymbol: item.currency.rCurrencySymbol,
        autoChargeDate: item.autoChargeDate === null ? 0 : item.autoChargeDate.time,
        invalidDateCSS: (item.autoChargeDate !== null && item.firstEligibleAutoChargeDate !== null && item.autoChargeDate.time < getEligibleDate(item.firstEligibleAutoChargeDate)) ? 'has-note' : '',
      }));

      setTableData(limitedFields);

      const checkForRemoveDates = (element) => element.autoChargeDate > 0;
      setShowRemoveLink(limitedFields.some(checkForRemoveDates));
    }
  }, [invoices]);

  const { exec: execSetAutoCharge, data: autochargePostData } = useAxios(
    () => '/app/invoice/setAutoCharge',
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  useEffect(() => {
    const firstDate = new Date();
    firstDate.setDate(firstDate.getDate() + 2);
    setStartDate(firstDate);

    const lastDate = new Date();
    lastDate.setDate(lastDate.getDate() + 7);
    setEndDate(lastDate);

    setShowNewDates(false);

    if (tableData.length > 0) {
      // check if some invoices have autoChargeDate, get earliest date
      let someHaveDates = true;
      let firstDateInArray = '';
      const acDateArray = [];
      tableData.forEach((item) => {
        if (item.autoChargeDate !== '') {
          acDateArray.push(item.autoChargeDate);
        }
      });
      acDateArray.sort((a, b) => a - b);

      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].autoChargeDate !== null && tableData[i].autoChargeDate !== '') {
          someHaveDates = true;
          firstDateInArray = new Date(acDateArray[0]);
          break;
        }
      }

      if (window.sessionStorage.getItem('autoChargeSource') === 'editOne'
      && tableData.length > 0 && tableData[0].autoChargeDate !== '' && tableData[0].autoChargeDate !== undefined) {
        setAutoChargeEnabledPage(true);
        setSelectedDate(new Date(tableData[0].autoChargeDate));
        setSelectedDateMillis(tableData[0].autoChargeDate !== null && tableData[0].autoChargeDate !== '' && tableData[0].autoChargeDate !== undefined ? tableData[0].autoChargeDate : '');
        setShowNewDates(false);
      } else if (someHaveDates) {
        setAutoChargeEnabledPage(true);
        setSelectedDate(new Date(firstDateInArray));
        setSelectedDateMillis(firstDateInArray !== null && firstDateInArray !== '' ? firstDateInArray.getTime() : '');
        setShowNewDates(false);
      }
    }
  }, [tableData]);

  useEffect(() => {
    let buttonText = '';
    if (!defaultDateChanged) {
      buttonText = 'Continue With No Changes';
    } else {
      buttonText = 'Save & Continue';
    }

    setSubmitButtonText(buttonText);
  }, [autoChargeEnabledPage, defaultDateChanged]);

  let backLabel = 'Invoice Group Details';
  let backURL = `${imagePath}/invoices/InvoiceGroupDetails/${invoiceGroupID}`;
  if (window.sessionStorage.getItem('autoChargeSource') === 'group') {
    backLabel = 'Invoice Group Details';
    backURL = `${imagePath}/invoices/InvoiceGroupDetails/${invoiceGroupID}`;
  } else if (window.sessionStorage.getItem('autoChargeSource') === 'create') {
    backLabel = 'Create New Invoices';
    backURL = `${imagePath}/invoices/CreateNewInvoices/${invoiceGroupID}`;
  } else if (window.sessionStorage.getItem('autoChargeSource').includes('edit')) {
    backLabel = 'Edit Invoices';
    backURL = `${imagePath}/invoices/EditInvoices/${invoiceGroupID}`;
  }

  useEffect(() => {
    setDisabledText(autoChargeEnabledPage ? '' : 'disabled-label');
  }, [autoChargeEnabledPage]);

  const AutoChargeButton = styled(Button)({
    fontSize: '1rem',
    textTransform: 'capitalize',
    backgroundColor: '#24988b',
    color: 'white',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#2ab1a2',
    },
  });

  const handleDateValue = (date) => {
    setSelectedDate(date);
    setSelectedDateMillis(date !== null ? date.getTime() : '');
    setShowNewDates(true);
    setSubmitDisabled(false);
    setDefaultDateChanged(true);
  };

  useEffect(() => {
    if ((selectedDate === '' || selectedDate === null || selectedDate === 'null') && autoChargeEnabledPage) {
      setSubmitDisabled(true);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (autochargePostData !== null && autochargePostData.status === 200) {
      if (isRemoveModalOpen) {
        window.location = groupURL;
      } else {
        window.location = previewURL;
      }
    }
  }, [autochargePostData]);

  const handleSubmitForm = () => {
    // if POST, redirect is triggered in useEffect above
    if ((selectedDateMillis !== '' && acInvoiceIdArray.length > 0)
      && ((autoChargeEnabledPage && showNewDates) || (isRemoveModalOpen))) {
      const dataObject = {
        autoChargeDate: (isRemoveModalOpen) ? null : selectedDateMillis,
        invoiceIDs: acInvoiceIdArray,
      };

      execSetAutoCharge({
        data: dataObject
      });
    } else {
      window.location = previewURL;
    }
  };

  const handleOnChangeInvoiceChecked = (e) => {
    const tempArray = acInvoiceIdArray;
    if (e.currentTarget.checked) {
      tempArray.push(Number(e.currentTarget.value));
      setIsEveryInvoiceChecked(apiInvoiceIdArray.length === tempArray.length);
      setAcInvoiceIdArray(tempArray);
    } else {
      const index = acInvoiceIdArray.indexOf(Number(e.currentTarget.value));
      tempArray.splice(index, 1);
      setAcInvoiceIdArray(tempArray);
      setIsEveryInvoiceChecked(false);
    }
    // confirm API array after above actions
    setApiInvoiceIdArray(invoices.map(item => item.invoiceID));
  };

  const handleOnCheckedAllInvoices = () => {
    if (acInvoiceIdArray.length === 0) {
      setAcInvoiceIdArray(apiInvoiceIdArray);
      setIsEveryInvoiceChecked(true);
    } else if (apiInvoiceIdArray.length === acInvoiceIdArray.length) {
      const blankArray = [];
      setAcInvoiceIdArray(blankArray);
      setIsEveryInvoiceChecked(false);
    } else {
      setAcInvoiceIdArray(apiInvoiceIdArray);
      setIsEveryInvoiceChecked(true);
    }
  };

  const openCalendar = () => {
    setShowCalendar(true);
  };

  const openRemoveModal = () => {
    setIsRemoveModalOpen(true);
    setAutoChargeEnabledPage(false);
  };

  if (isLoading || isIdle) {
    return (
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    );
  }

  return (
    <GoogleTagManagerProvider gtmId="GTM-5PWF2V">
      <Container>
        <Row className="mt-4">
          <Col xs={12}>
            <div>
              <a href={backURL}>
                <FontAwesomeIcon icon="angle-left" className="fa-solid color-white-bg" />  Back to {backLabel}
              </a>
            </div>
            <div className="mt-4">
              <h1 className="">
                Automatically charge buyers' credit cards
              </h1>
              <div>
                Once the invoices are sent, we will attempt to charge the buyers' credit cards on the selected date.
                <span className="ml-2">
                  <a href="https://www.invaluableauctions.com/inv/help-center/resource/invoicing/auto-charging-bidder-invoices/" target="_blank" rel="noopener noreferrer">How it Works</a>
                </span>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={8}>
            <div>
              <button
                className="btn btn-link btn-inline"
                onClick={(e) => openCalendar(e)}
                type="button"
              >
                Set Auto-Charge date for {(apiInvoiceIdArray.length === acInvoiceIdArray.length) ? 'ALL' : 'SELECTED'}
              </button>
              {showRemoveLink && (
                <button
                  className="btn btn-link btn-inline ml-5"
                  onClick={(e) => openRemoveModal(e)}
                  type="button"
                >
                  Remove Auto-Charge date for {(apiInvoiceIdArray.length === acInvoiceIdArray.length) ? 'ALL' : 'SELECTED'}
                </button>
              )}
            </div>
            {showCalendar && (
              <div className={`mt-4 ${disabledText}`}>
                Auto-Charge Date: &nbsp;
                <DatePicker
                  id="eventDate"
                  showIcon
                  icon={faCalendar}
                  disabled={!autoChargeEnabledPage}
                  selected={selectedDateMillis > 0 ? selectedDate : ''}
                  minDate={startDate}
                  onChange={date => handleDateValue(date)}
                  maxDate={endDate}
                  placeholderText="Click to select date"
                  isClearable
                  startOpen={true}
                />
              </div>
            )}
          </Col>
          <Col xs={4}>
            <span className="cursor-not-allowed">
              <AutoChargeButton
                variant="contained"
                onClick={() => handleSubmitForm()}
                disabled={submitDisabled}
              >
                {submitButtonText}
              </AutoChargeButton>
            </span>
            {submitDisabled && autoChargeEnabledPage && (
              <div className="mt-2 error-message">
                Select a new date to continue
              </div>
            )}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={12}>
            <div className="my-2 italic-text">
              Displaying invoices eligible for Auto-Charge
              <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" id="autoChargeInfoIcon" />
            </div>
            <UncontrolledTooltip placement="right" target="autoChargeInfoIcon" className="name-tooltip other-tooltip">
              Some invoices are not eligible for autocharge. Reasons include invoice is payment restricted, paid, payment pending, credit card limit exceeded, etc.
            </UncontrolledTooltip>
          </Col>

        </Row>
        <Row className="mt-0">
          <Col xs={12}>
            <ReactTable
              className="-highlight mt-0 rt-table-invoice"
              minRows={1}
              data={tableData}
              defaultPageSize={50}
              pageSizeOptions={[50, 100, 200]}
              columns={AutoChargeTableCols(showNewDates,
                selectedDate,
                formatDate,
                handleOnChangeInvoiceChecked,
                handleOnCheckedAllInvoices,
                isEveryInvoiceChecked,
                acInvoiceIdArray)}
              rowsText="per page"
              noDataText="No invoices have been generated"
              showPageJump={false}
            />
          </Col>
        </Row>
      </Container>
      <RemoveAutoChargeModal
        isOpen={isRemoveModalOpen}
        setIsRemoveModalOpen={setIsRemoveModalOpen}
        handleSubmitRemove={handleSubmitForm}
      />
    </GoogleTagManagerProvider>
  );
};

export default InvoiceAutoCharge;
